import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  Button,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

interface PlanViewerModalProps {
  open: boolean;
  onClose: () => void;
  imageUrl: string;
  title?: string;
  description?: string;
}

const PlanViewerModal: React.FC<PlanViewerModalProps> = ({
  open,
  onClose,
  imageUrl,
  title = 'Floor Plan', // Default title
  description,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm')); // Use full screen for small devices

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="lg" // Allow wider modal for large plans
      PaperProps={{
        sx: {
          // Ensure dialog does not exceed viewport height, especially on mobile
          maxHeight: fullScreen ? '100vh' : `calc(100vh - ${theme.spacing(8)})`,
          display: 'flex',
          flexDirection: 'column',
        }
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
          {title}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
            p: 2,
            flexGrow: 1, // Allow content to grow
            overflowY: 'auto', // Ensure content itself is scrollable if needed
            display: 'flex', // Center image
            flexDirection: 'column', // Stack image and description
            alignItems: 'center', // Center image horizontally
            justifyContent: 'center' // Center image vertically if not too tall
        }}
      >
        <Box
          component="img"
          src={imageUrl}
          alt={description || title}
          sx={{
            maxWidth: '100%',
            maxHeight: {
                // Adjust max height to leave space for title and description if present
                xs: description ? 'calc(100vh - 180px)' : 'calc(100vh - 120px)', // Smaller for mobile
                sm: description ? 'calc(100vh - 200px)' : `calc(100vh - 150px - ${theme.spacing(8)})`
            },
            objectFit: 'contain',
            display: 'block', // Remove extra space below img
            mb: description ? 2 : 0, // Margin bottom if description exists
          }}
        />
        {description && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center', width: '100%' }}>
            {description}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ p:1, borderTop: `1px solid ${theme.palette.divider}` }}>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PlanViewerModal;
